import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { showConfirmDialog } from "../../Logic/ConfirmDialog/showConfirmDialog";
import {
    Departments,
    GetDepartments,
    GetMailboxes,
    GetMailboxRoles,
    MailboxList,
    MailboxRolesListItemDTO,
} from "../../Logic/EmsAPI/mailboxAccessRequest";
import { RootState, useAppDispatch } from "../../state/store";
import { setUser } from "../../state/userSlice";
import DropdownWithQuickFind from "../Generic/Form/DropDownWithQuickFind";
import TextBox from "../Generic/Form/TextBox";
import "./styles.scss";
import { GetUserProfileData, GetUserResponse, UpdateUser } from "./tenantApiCall";

function EditProfile() {
    const UserInfo = useSelector((state: RootState) => state.UserInformation);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userProfile, setUserProfile] = useState<GetUserResponse>();
    const [mailboxes, setMailboxes] = useState<Array<MailboxList>>([]);
    const [mailboxRoles, setMailboxRoles] = useState<Array<MailboxRolesListItemDTO>>([]);
    const [departments, setDepartments] = useState<Array<Departments>>([]);
    const dispatch = useAppDispatch();
    useEffect(() => {
        (async () => {
            await loadData();
        })();
    }, []);

    async function loadData() {
        const mailboxes_results = GetMailboxes();
        const mailbox_roles_results = GetMailboxRoles();
        const departments_results = GetDepartments();
        const profileResponse = await GetUserProfileData(UserInfo.userID);
        Promise.all([mailboxes_results, mailbox_roles_results, departments_results, profileResponse]).then(results => {
            if (results[0].isOk) {
                setMailboxes(results[0].data?.items!);
            }
            if (results[1].isOk) {
                setMailboxRoles(results[1].data?.items!);
            }
            if (results[2].isOk) {
                setDepartments(results[2].data?.items!);
            }
            if (results[3].isOk && results[3].data) {
                let profile = { email: "", jobTitle: "" };
                if (results[3].data.profile) {
                    profile = JSON.parse(results[3].data.profile);
                } else {
                    profile.email = UserInfo.email ?? "";
                    profile.jobTitle = UserInfo.jobTitle ?? "";
                }
                results[3].data.profile_email = profile.email;
                results[3].data.profile_jobTitle = profile.jobTitle;
                setUserProfile(results[3].data);
            }
            setIsLoading(false);
        });
    }
    function getDepartmentIdByName(departmentName?: string): number {
        return departments.find(department => department.name === departmentName)?.id ?? 0;
    }
    function handleFieldChange(field: keyof GetUserResponse) {
        return (newValue: string) => {
            setUserProfile(prev =>
                prev
                    ? {
                          ...prev,
                          [field]: typeof prev[field] === "number" ? Number(newValue) : newValue,
                      }
                    : prev,
            );
        };
    }
    function handleDepartmentChange(newValue: string) {
        const departmentName = departments.find(department => department.id.toString() === newValue)?.name ?? "";
        setUserProfile(prev => (prev ? { ...prev, departmentName, departmentID: Number(newValue) } : prev));
    }
    function getMissingRequiredFields(profile: GetUserResponse): string[] {
        const missing: string[] = [];
        if (!profile.firstName) missing.push("Firstname");
        if (!profile.lastName) missing.push("Lastname");
        if (!profile.email) missing.push("Email Address");
        if (getDepartmentIdByName(profile.departmentName) <= 0) missing.push("Department");
        if (!profile.externalUserID) missing.push("External Username");
        if (!profile.profile_email) missing.push("Profile Email Address");
        if (!profile.telephone) missing.push("Telephone Number");
        if (!profile.mobile) missing.push("Mobile Number");
        if (!profile.profile_jobTitle) missing.push("Business Title");
        return missing;
    }

    return (
        <>
            <div className="header">
                Edit Details
                <div style={{ textAlign: "right", float: "right", marginRight: "10px" }}>
                    <button
                        className="g-btn-action"
                        style={{ fontSize: "9pt", textAlign: "right" }}
                        onClick={async () => {
                            if (!userProfile) return;
                            const missingFields = getMissingRequiredFields(userProfile);
                            if (missingFields.length > 0) {
                                await showConfirmDialog(
                                    `The following fields are required: ${missingFields.join(", ")}`,
                                    { messageType: "warn", showCancel: false },
                                );
                                return;
                            }
                            let uUser = JSON.parse(JSON.stringify(userProfile));
                            uUser["profile"] = JSON.stringify({
                                email: uUser.profile_email,
                                jobTitle: uUser.profile_jobTitle,
                            });
                            UpdateUser(userProfile.id, uUser).then(response => {
                                if (response.isOk) {
                                    const stateUser = {
                                        ...UserInfo,
                                        profile: { email: uUser.profile_email, jobTitle: uUser.profile_jobTitle },
                                    };
                                    dispatch(setUser(stateUser));
                                    alert("Profile updated successfully.");
                                } else {
                                    alert("Failed to update profile.");
                                }
                            });
                        }}
                    >
                        {" "}
                        Update
                    </button>
                </div>
            </div>
            <div style={{ padding: "20px", paddingTop: "13px" }}>
                {isLoading && <div>Loading...</div>}
                {!isLoading && userProfile && (
                    <>
                        <div className="row">
                            <TextBox
                                id="firstName"
                                value={userProfile.firstName}
                                onChange={handleFieldChange("firstName")}
                                maxLength={56}
                                label="Firstname"
                            ></TextBox>
                            <TextBox
                                id="lastName"
                                value={userProfile?.lastName}
                                onChange={handleFieldChange("lastName")}
                                maxLength={56}
                                label="Lastname"
                            ></TextBox>
                            <TextBox
                                id="emailAddress"
                                value={userProfile?.email}
                                onChange={handleFieldChange("email")}
                                maxLength={56}
                                label="Title"
                            ></TextBox>
                            <DropdownWithQuickFind
                                id="departmentId"
                                label="Department"
                                value={
                                    getDepartmentIdByName(userProfile?.departmentName) === 0
                                        ? ""
                                        : getDepartmentIdByName(userProfile?.departmentName).toString()
                                }
                                returnFunc={handleDepartmentChange}
                                Valid={true}
                                options={departments.map(item => ({ text: item.name, value: item.id.toString() }))}
                            ></DropdownWithQuickFind>
                            <TextBox
                                id="externalUserID"
                                value={userProfile?.externalUserID}
                                onChange={handleFieldChange("externalUserID")}
                                maxLength={56}
                                label="External Username"
                                disabled={true}
                            ></TextBox>
                            <TextBox
                                id="profile_email"
                                value={userProfile?.profile_email}
                                onChange={handleFieldChange("profile_email")}
                                maxLength={56}
                                label="Email Address"
                            ></TextBox>
                            <TextBox
                                id="telephone"
                                value={userProfile?.telephone}
                                onChange={handleFieldChange("telephone")}
                                maxLength={56}
                                label="Telephone Number"
                            ></TextBox>

                            <TextBox
                                id="mobileNumber"
                                value={userProfile?.mobile}
                                onChange={handleFieldChange("mobile")}
                                maxLength={56}
                                label="Mobile Number"
                            ></TextBox>
                            <TextBox
                                id="profile_jobTitle"
                                value={
                                    userProfile?.profile_jobTitle === undefined
                                        ? UserInfo.jobTitle
                                        : userProfile?.profile_jobTitle
                                }
                                onChange={handleFieldChange("profile_jobTitle")}
                                maxLength={56}
                                label="Job Title"
                            ></TextBox>
                        </div>
                        <br></br>
                    </>
                )}
            </div>
        </>
    );
}
export default EditProfile;
